/**
 * One component per question type.
 *
 * None of them has a Back button, deliberately: being able to step back one
 * screen would let a child see an answer and retry it, and then
 * `is_first_attempt` — which every mastery number rests on — would mean
 * nothing. Every one of them DOES have a way out that is not "get it right":
 * skip. A trial you cannot fail measures nothing, which is exactly how the old
 * Pygame app ended up with 83,271 rows and not one recorded mistake.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Question } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card } from '../components/ui'
import { say, sayInFirstLanguage, type SpeechState } from '../lib/speech'
import { HAS_KEYBOARD, useTrialKeys } from '../lib/keys'

export interface TrialProps {
  q: Question
  firstLanguage: string
  onAnswer: (response: unknown, extras?: Record<string, unknown>) => void
  busy: boolean
}

// ------------------------------------------------------------------ helpers

/**
 * The help buttons, grouped into hear-it and read-it.
 *
 * A group with nothing in it is not rendered at all. Showing a "Hear it"
 * heading with no button under it, which is what happened for every imported
 * word without a translation, just looks broken.
 */
/**
 * The "2" beside an option on a laptop.
 *
 * Shown only where there is a keyboard. On a tablet it would be a number
 * that does nothing, and a seven-year-old reading "1 2 3 4" down the side of
 * four answers may well think the numbers are part of the question.
 */
function KeyBadge({ index }: { index: number }) {
  if (!HAS_KEYBOARD) return null
  return (
    <span
      aria-hidden="true"
      style={{
        flex: '0 0 auto',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 24, height: 24, marginRight: 12,
        fontSize: 13, fontWeight: 800, color: theme.faint,
        background: theme.bg, border: `1.5px solid ${theme.border}`,
        borderRadius: radius.sm,
      }}
    >
      {index + 1}
    </span>
  )
}


/** "Press 1-4 to pick, Enter to check." Only where those keys exist. */
function KeyHint({ text }: { text: string }) {
  if (!HAS_KEYBOARD) return null
  return (
    <div style={{
      fontSize: 13, color: theme.faint, textAlign: 'center', marginTop: 10,
    }}>
      {text}
    </div>
  )
}


function HelpRow({
  speak, translation, example, definition, firstLanguage, onUsed,
  meaningAlreadyShown = false,
}: {
  speak?: string | null
  translation?: string | null
  example?: string | null
  definition?: string | null
  firstLanguage: string
  onUsed: (what: 'heard' | 'translation' | 'example') => void
  /**
   * The meaning is already printed on the card, so do not offer a button
   * that shows it again.
   *
   * Cued recall prints the definition at the top as the hint — it is how a
   * child knows which word is meant. Offering "What it means" underneath put
   * the same sentence on screen twice, one of them behind a button, which
   * reads as though the second one might say something different.
   */
  meaningAlreadyShown?: boolean
}) {
  const [showExample, setShowExample] = useState(false)
  const [showMeaning, setShowMeaning] = useState(false)
  void meaningAlreadyShown

  const chip = (label: string, onClick: () => void, on = false) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'inherit', fontSize: 15, fontWeight: 600,
        color: on ? theme.orangeDark : theme.text2,
        background: on ? theme.orangePale : '#FFFFFF',
        border: `1.5px solid ${on ? theme.orange : theme.border}`,
        borderRadius: radius.sm, padding: '9px 13px',
        minHeight: TAP_MIN, cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )

  const hear = [
    speak ? chip('🔊 Say it in English', () => { say(speak); onUsed('heard') }) : null,
    translation
      ? chip(`🔊 ${translation}`, () => {
          // Speaks the WORD in the child's own language, not a sentence.
          sayInFirstLanguage(translation, firstLanguage)
          onUsed('translation')
        })
      : null,
  ].filter(Boolean)

  const read = [
    definition && !meaningAlreadyShown
      ? chip('What it means', () => setShowMeaning((v) => !v), showMeaning)
      : null,
    example ? chip('In a sentence', () => { setShowExample((v) => !v); onUsed('example') }, showExample) : null,
  ].filter(Boolean)

  if (!hear.length && !read.length) return null

  return (
    <div style={{ marginTop: 18 }}>
      {hear.length ? (
        <>
          <div style={{ fontSize: 13, color: theme.faint, marginBottom: 6 }}>Hear it</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: read.length ? 14 : 0 }}>
            {hear}
          </div>
        </>
      ) : null}

      {read.length ? (
        <>
          <div style={{ fontSize: 13, color: theme.faint, marginBottom: 6 }}>Read it</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{read}</div>
        </>
      ) : null}

      {showMeaning && definition ? (
        <p style={{
          margin: '12px 0 0', padding: '10px 12px', background: theme.bg,
          borderRadius: 8, fontSize: 16, color: theme.text2, lineHeight: 1.5,
        }}>
          {definition}
        </p>
      ) : null}
      {showExample && example ? (
        <p style={{
          margin: '10px 0 0', padding: '10px 12px', background: theme.bg,
          borderRadius: 8, fontSize: 16, color: theme.text2, lineHeight: 1.5,
          fontStyle: 'italic',
        }}>
          {example}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Start over, and I don't know.
 *
 * Both are trial-level: one resets this question, the other gives up on it.
 * They sit together on the right, away from Check, because Check is the thing
 * a child is aiming at and these two are the ways out. Having them at
 * opposite ends of the card made them look like different kinds of control.
 */
function TrialActions({
  onReset, onSkip, busy, resetLabel = 'Start over', resetDisabled = false,
}: {
  onReset?: () => void; onSkip: () => void; busy: boolean
  resetLabel?: string; resetDisabled?: boolean
}) {
  const link = (label: string, onClick: () => void, off = false) => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || off}
      style={{
        fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: theme.muted,
        background: 'transparent', border: `1px solid ${theme.border}`,
        borderRadius: radius.sm, padding: '8px 14px',
        minHeight: TAP_MIN, cursor: busy ? 'default' : 'pointer',
      }}
    >
      {label}
    </button>
  )
  return (
    <div style={{
      display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap',
      justifyContent: 'flex-end',
    }}>
      {onReset ? link(resetLabel, onReset, resetDisabled) : null}
      {link("I don't know", onSkip)}
    </div>
  )
}

const KEYS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm']

/** An on-screen keyboard, so a tablet with no hardware keys still works. */
function Keyboard({
  onKey, onBack, onClear,
}: { onKey: (c: string) => void; onBack: () => void; onClear: () => void }) {
  const key = (label: string, onClick: () => void, wide = false) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'inherit', fontSize: 17, fontWeight: 600, color: theme.text,
        background: '#FFFFFF', border: `1.5px solid ${theme.border}`,
        borderRadius: radius.sm, minHeight: TAP_MIN,
        // A letter key has NO minimum width. Ten of them at 32px plus gaps
        // came to 365px, and a 360px phone has about 296px to give — so the
        // bottom row ran off the screen. They share whatever there is
        // instead; the 44px height keeps them tappable either way.
        minWidth: wide ? 68 : 0,
        flex: wide ? '0 0 auto' : '1 1 0', padding: '10px 2px',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {KEYS.map((row) => (
        <div key={row} style={{ display: 'flex', gap: 4, minWidth: 0 }}>
          {row.split('').map((c) => key(c, () => onKey(c)))}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
        {key('⌫', onBack, true)}
        {key('Clear', onClear, true)}
      </div>
    </div>
  )
}

// ----------------------------------------------------------------- learning

/** Meeting a word. No answer box — this one is not a test. */
export function Learning({ q, firstLanguage, onAnswer, busy }: TrialProps) {
  const word = q.payload.word as string
  const [heard, setHeard] = useState(false)

  // Say it once on arrival. Hearing the word is the point of this trial.
  useEffect(() => { say(word); setHeard(true) }, [word])

  // Nothing to choose here, so Enter just means "go on".
  useTrialKeys({
    busy,
    onSubmit: () => onAnswer(null, { heard_word: heard }),
  })

  return (
    <Card>
      <div style={{ fontSize: 14, color: theme.faint, marginBottom: 10 }}>A new word</div>
      <div style={{
        fontSize: 40, fontWeight: 800, color: theme.text, letterSpacing: 0.5,
        marginBottom: 6, wordBreak: 'break-word',
      }}>
        {word}
      </div>
      {q.payload.definition ? (
        <p style={{ margin: '0 0 4px', fontSize: 17, color: theme.text2, lineHeight: 1.5 }}>
          {q.payload.definition}
        </p>
      ) : null}
      {q.payload.example ? (
        <p style={{ margin: '12px 0 0', fontSize: 16, color: theme.muted, lineHeight: 1.55, fontStyle: 'italic' }}>
          {q.payload.example}
        </p>
      ) : null}

      <HelpRow
        speak={word}
        translation={q.payload.translation}
        firstLanguage={firstLanguage}
        onUsed={(what) => { if (what === 'heard') setHeard(true) }}
      />

      <div style={{ marginTop: 22 }}>
        <Button full tone="child" disabled={busy}
                onClick={() => onAnswer(null, { heard_word: heard })}>
          Got it
        </Button>
        <KeyHint text="Press Enter to carry on" />
      </div>
    </Card>
  )
}

// -------------------------------------------------------------- cued recall

interface Tile { id: number; letter: string }

/**
 * 'a p _ l _' with the missing letters below.
 *
 * Three things changed after Sam used it:
 *
 *  1. Any letter can be placed, right or wrong. The earlier rule — accept only
 *     the letter that is correct next — meant a wrong tap did nothing at all,
 *     so a child who could not work it out had no way forward except guessing
 *     every combination. A trial you cannot fail also cannot measure anything.
 *     Wrong placements are still counted, in wrong_key_presses.
 *  2. Tiles carry an id, not just a letter. Filtering the bank by letter left
 *     an extra tile on screen whenever a word had the same letter twice —
 *     'containment' has three n's and showed all three after one was used.
 *  3. Drag a tile onto a blank, or tap it. Tap a filled blank to take it back.
 */
export function CuedRecall({ q, firstLanguage, onAnswer, busy }: TrialProps) {
  const slots = q.payload.slots as (string | null)[]
  const order = (q.payload.order ?? []) as string[]

  const tiles: Tile[] = useMemo(
    () => ((q.payload.bank ?? []) as string[]).map((letter, id) => ({ id, letter })),
    [q.payload.bank],
  )

  // For each slot: the id of the tile sitting in it, or null.
  const [placed, setPlaced] = useState<(number | null)[]>(() => slots.map(() => null))
  const [wrongTaps, setWrongTaps] = useState(0)
  const [dragging, setDragging] = useState<number | null>(null)

  const byId = useMemo(() => new Map(tiles.map((t) => [t.id, t])), [tiles])
  const inBank = tiles.filter((t) => !placed.includes(t.id))
  const blanks = slots.map((s, i) => (s === null ? i : -1)).filter((i) => i >= 0)
  const nextBlank = blanks.find((i) => (placed[i] ?? null) === null) ?? -1
  const done = blanks.every((i) => (placed[i] ?? null) !== null)

  const wordSoFar = () =>
    slots
      .map((s, i) => {
        if (s !== null) return s
        const id = placed[i] ?? null
        return id === null ? '' : byId.get(id)?.letter ?? ''
      })
      .join('')

  const put = (tileId: number, slotIndex: number) => {
    if (busy || slots[slotIndex] !== null) return
    // Was this the letter that belonged here? Recorded, never blocked.
    const which = blanks.indexOf(slotIndex)
    const expected = order[which]
    if (expected && byId.get(tileId)?.letter.toLowerCase() !== expected.toLowerCase()) {
      setWrongTaps((n) => n + 1)
    }
    setPlaced((prev) => {
      const next = [...prev]
      const already = next.indexOf(tileId)      // move, do not clone
      if (already >= 0) next[already] = null
      next[slotIndex] = tileId
      return next
    })
  }

  const takeBack = (slotIndex: number) => {
    if (busy) return
    setPlaced((prev) => {
      const next = [...prev]
      next[slotIndex] = null
      return next
    })
  }

  const reset = () => setPlaced(slots.map(() => null))

  // Typing still works. Drag and drop is nice on a tablet, but a keyboard is
  // faster for anyone who has one, and taking it away would make the trial
  // slower for exactly the child who finds it easy.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (busy || e.ctrlKey || e.metaKey || e.altKey) return
      if (e.key === 'Enter') {
        // Every blank filled: Enter checks it. This trial has its own key
        // handler rather than useTrialKeys because a letter here places a
        // tile instead of picking an option.
        if (!done) return
        e.preventDefault()
        onAnswer(wordSoFar(), { wrong_key_presses: wrongTaps, assisted: true })
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        reset()
        return
      }
      if (e.key === 'Backspace') {
        e.preventDefault()
        const filled = blanks.filter((i) => (placed[i] ?? null) !== null)
        const last = filled[filled.length - 1]
        if (last !== undefined) takeBack(last)
        return
      }
      if (e.key.length !== 1 || !/[a-z]/i.test(e.key)) return
      const target = blanks.find((i) => (placed[i] ?? null) === null)
      if (target === undefined) return
      const tile = tiles.find(
        (x) => !placed.includes(x.id) && x.letter.toLowerCase() === e.key.toLowerCase(),
      )
      // A letter that is not in the bank cannot be part of the word, so there
      // is nothing to place. The on-screen tiles already say which are left.
      if (!tile) { setWrongTaps((w) => w + 1); return }
      e.preventDefault()
      put(tile.id, target)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <Card>
      <div style={{ fontSize: 14, color: theme.faint, marginBottom: 4 }}>
        {q.payload.hint ? 'Spell the word that means…' : 'Fill in the missing letters'}
      </div>
      {q.payload.hint ? (
        <div style={{
          margin: '0 0 12px', padding: '10px 12px', background: theme.bg,
          borderRadius: radius.sm, fontSize: 16, color: theme.text2, lineHeight: 1.45,
        }}>
          {q.payload.hint as string}
        </div>
      ) : null}
      <div style={{ fontSize: 13, color: theme.fainter, marginBottom: 14 }}>
        Type it, drag a letter, or tap one. Tap a letter you have placed to
        take it back.
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {slots.map((given, i) => {
          const tileId = placed[i] ?? null
          const shown = given !== null ? given : (tileId !== null ? byId.get(tileId)?.letter : null)
          const isSpace = given === ' '
          const fillable = given === null
          return (
            <div
              key={i}
              onDragOver={(e) => { if (fillable) e.preventDefault() }}
              onDrop={(e) => {
                e.preventDefault()
                if (dragging !== null) put(dragging, i)
                setDragging(null)
              }}
              onClick={() => { if (fillable && tileId !== null) takeBack(i) }}
              style={{
                width: isSpace ? 18 : 40, height: 52,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, fontWeight: 800,
                color: fillable ? theme.text : theme.faint,
                background: isSpace ? 'transparent'
                  : !fillable ? '#FFFFFF'
                  : tileId !== null ? theme.orangePale : theme.bg,
                border: isSpace ? 'none'
                  : `2px solid ${i === nextBlank ? theme.orange : theme.border}`,
                borderRadius: radius.sm,
                cursor: fillable && tileId !== null ? 'pointer' : 'default',
              }}
            >
              {isSpace ? '' : (shown ?? '')}
            </div>
          )
        })}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 8,
      }}>
        <span style={{ fontSize: 13, color: theme.faint }}>
          {inBank.length ? 'Letters to place' : 'All placed'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', minHeight: TAP_MIN }}>
        {inBank.map((tile) => (
          <button
            key={tile.id}
            type="button"
            draggable
            onDragStart={() => setDragging(tile.id)}
            onDragEnd={() => setDragging(null)}
            onClick={() => { if (nextBlank >= 0) put(tile.id, nextBlank) }}
            style={{
              fontFamily: 'inherit', fontSize: 20, fontWeight: 700, color: theme.text,
              background: '#FFFFFF',
              border: `2px solid ${dragging === tile.id ? theme.orange : theme.border}`,
              borderRadius: radius.sm, minWidth: TAP_MIN, minHeight: TAP_MIN,
              cursor: 'grab',
            }}
          >
            {tile.letter}
          </button>
        ))}
      </div>

      <HelpRow
        speak={q.payload.speak}
        translation={q.payload.translation}
        definition={q.payload.definition}
        example={q.payload.example}
        firstLanguage={firstLanguage}
        onUsed={() => undefined}
        meaningAlreadyShown={Boolean(q.payload.hint)}
      />

      <div style={{ marginTop: 22 }}>
        <Button
          full tone="child" disabled={!done || busy}
          onClick={() => onAnswer(wordSoFar(), {
            wrong_key_presses: wrongTaps,
            assisted: true,   // letters were on screen; this is not free recall
          })}
        >
          {done ? 'Check' : 'Fill every blank first'}
        </Button>
        <KeyHint text="Type the letters, then Enter to check" />
        <TrialActions
          busy={busy}
          onReset={reset}
          resetLabel="↺ Start over"
          resetDisabled={blanks.every((i) => (placed[i] ?? null) === null)}
          onSkip={() => onAnswer(null, { skipped: true, wrong_key_presses: wrongTaps })}
        />
      </div>
    </Card>
  )
}

// -------------------------------------------------------------- free recall

/** Hear it, type it. No letters shown. */
export function FreeRecall({ q, firstLanguage, onAnswer, busy }: TrialProps) {
  // `speak` rather than `word`: the browser's voice needs the text. It becomes
  // an audio URL once Google Text-to-Speech is wired in.
  const word = q.payload.speak as string | undefined
  const [typed, setTyped] = useState('')
  const [heard, setHeard] = useState(false)
  // The one question that cannot be answered without the sound, so it shows
  // what the voice is doing and gives a way on when it fails (build list
  // item 2, 25 Oct 2026; sound that fails was users' commonest complaint).
  const [audio, setAudio] = useState<SpeechState | 'idle'>('idle')
  // One listener for every play, so speech.ts can tell a replay (ignore the
  // old try) from a word cut off by another button (the old try is done).
  const onSound = useCallback((s: SpeechState) => {
    setAudio(s)
    if (s === 'speaking') setHeard(true)
  }, [])
  const play = (w: string) => say(w, 'en-US', 0.9, onSound)

  useEffect(() => { setAudio('idle'); if (word) play(word) }, [word])
  const letters = word ? word.replace(/[^a-z]/gi, '').length : 0

  // Enter used to live on the input, which meant it stopped working the
  // moment the child used the on-screen keyboard and focus left the box.
  useTrialKeys({
    busy,
    onSubmit: () => {
      if (typed.trim()) onAnswer(typed.trim(), { heard_word: heard })
    },
    onClear: () => setTyped(''),
  })

  return (
    <Card>
      <div style={{ fontSize: 14, color: theme.faint, marginBottom: 12 }}>
        Listen, then spell it
      </div>

      <button
        type="button"
        onClick={() => { if (word) play(word) }}
        style={{
          fontFamily: 'inherit', fontSize: 18, fontWeight: 700, color: '#FFFFFF',
          background: theme.orange, border: 'none', borderRadius: radius.md,
          padding: '14px 22px', minHeight: 56, cursor: 'pointer', width: '100%',
        }}
      >
        {audio === 'speaking' ? '🔊 Playing…' : audio === 'failed' ? '🔊 Try the sound again' : '🔊 Play the word'}
      </button>

      {audio === 'failed' && word ? (
        <div role="status" style={{
          margin: '12px 0 0', padding: '10px 12px', background: theme.orangePale,
          border: `1px solid ${theme.orange}`, borderRadius: radius.sm,
          fontSize: 16, color: theme.text, lineHeight: 1.45,
        }}>
          The sound did not play. Here is a clue instead: it starts with{' '}
          <strong>{word.charAt(0).toLowerCase()}</strong> and has <strong>{letters}</strong> letters.
          {q.payload.definition && !q.payload.hint ? <> It means: {q.payload.definition as string}</> : null}
        </div>
      ) : null}

      {q.payload.hint ? (
        <div style={{
          margin: '16px 0 0', padding: '10px 12px', background: theme.bg,
          borderRadius: radius.sm, fontSize: 16, color: theme.text2, lineHeight: 1.45,
        }}>
          {q.payload.hint as string}
        </div>
      ) : null}

      <input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoFocus
        placeholder="Type it here"
        // The letters on screen below are the keyboard. inputMode "none" keeps
        // a tablet's own keyboard shut, and with it the word suggestions and
        // autocorrect it would offer (Sam, 22 Oct 2026: the iPad was helping
        // with the spelling). A real keyboard on a laptop still types here.
        inputMode="none"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        style={{
          width: '100%', boxSizing: 'border-box', marginTop: 16,
          fontFamily: 'inherit', fontSize: 24, fontWeight: 700, textAlign: 'center',
          letterSpacing: 1, color: theme.text, background: '#FFFFFF',
          border: `2px solid ${theme.border}`, borderRadius: radius.md,
          padding: '14px 12px', minHeight: 60,
        }}
      />

      <Keyboard
        onKey={(c) => setTyped((t) => t + c)}
        onBack={() => setTyped((t) => t.slice(0, -1))}
        onClear={() => setTyped('')}
      />

      <HelpRow
        translation={q.payload.translation}
        definition={q.payload.definition}
        firstLanguage={firstLanguage}
        onUsed={() => undefined}
        meaningAlreadyShown={Boolean(q.payload.hint)}
      />

      <div style={{ marginTop: 22 }}>
        <Button full tone="child" disabled={!typed.trim() || busy}
                onClick={() => onAnswer(typed.trim(), { heard_word: heard })}>
          Check
        </Button>
        <TrialActions
          busy={busy}
          onReset={typed ? () => setTyped('') : undefined}
          resetLabel="Clear"
          onSkip={() => onAnswer(null, { skipped: true, heard_word: heard })}
        />
        <KeyHint text="Press Enter to check" />
      </div>
    </Card>
  )
}

// ---------------------------------------------------------- multiple choice

export function MultipleChoice({ q, onAnswer, busy }: TrialProps) {
  const [picked, setPicked] = useState<number | null>(null)
  const options = (q.payload.options ?? []) as string[]

  // `prompt` asks the question ("Which ending sounds right?") and `stem` is
  // the thing being asked about ("We anticipate _____"). Showing only the
  // first match left the collocation questions with nothing to answer.
  const prompt = (q.payload.sentence ?? q.payload.prompt ?? q.payload.question ?? '') as string
  const stem = (q.payload.stem ?? '') as string
  // "Goes with" questions, asked one clear way whatever the stored wording
  // (Sam, 9 Oct 2026: "the goes with question is always confusing").
  const together = q.item_type === 'collocation' ? collocationTarget(prompt) : null
  const goesWith = q.item_type === 'collocation' && /goes\s/i.test(prompt)

  useTrialKeys({
    options: options.length, picked, busy,
    onPick: setPicked,
    onSubmit: () => onAnswer(picked),
    onClear: () => setPicked(null),
  })

  return (
    <Card>
      {q.item_type === 'analogy' && q.payload.pair ? (
        <div style={{
          marginBottom: 16, padding: 14, background: theme.greenPale,
          borderRadius: radius.md, border: `1px solid ${theme.green}33`,
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: theme.greenDarker }}>
            {(q.payload.pair as string[])[0]} → {(q.payload.pair as string[])[1]}
          </div>
          <div style={{ fontSize: 15, color: theme.greenDark, marginTop: 4 }}>
            The link: {q.payload.relation as string}
          </div>
        </div>
      ) : null}

      {q.item_type === 'reading' && q.payload.passage ? (
        <div style={{
          marginBottom: 18, padding: 16, background: theme.bg,
          borderRadius: radius.md, maxHeight: 320, overflowY: 'auto',
        }}>
          {q.payload.title ? (
            <div style={{ fontWeight: 800, fontSize: 17, color: theme.text, marginBottom: 8 }}>
              {q.payload.title as string}
            </div>
          ) : null}
          {(q.payload.passage as string).split('\n\n').map((para, i) => (
            <p key={i} style={{ margin: '0 0 12px', fontSize: 16, lineHeight: 1.65, color: theme.text2 }}>
              {para}
            </p>
          ))}
        </div>
      ) : null}

      {/* A definition question's prompt is only the meaning ("far away from
          anywhere else"). Sam, 8 Oct 2026: with so many kinds of question,
          it needs saying what to do. So it is asked as a question. */}
      {q.item_type === 'definition' ? (
        <div style={{ fontSize: 15, fontWeight: 800, color: theme.muted, margin: '0 0 4px' }}>
          Which word means…
        </div>
      ) : null}
      {goesWith ? (
        <div style={{ fontSize: 15, fontWeight: 800, color: theme.muted, margin: '0 0 4px' }}>
          Words that go together
        </div>
      ) : null}
      <p style={{ margin: '0 0 6px', fontSize: 19, color: theme.text, lineHeight: 1.5, fontWeight: 600 }}>
        {q.item_type === 'analogy' && q.payload.question_word
          ? `${q.payload.question_word as string} → ?`
          : q.item_type === 'definition' ? `“${prompt.replace(/[.?]$/, '')}”?`
          : together ? <>Which word is often said together with <b>{together}</b>?</>
          : prompt}
      </p>
      {goesWith ? (
        <p style={{ margin: '0 0 4px', fontSize: 15, color: theme.muted }}>
          Like {examplesFor(prompt, options).map(([a, b], i, all) => (
            <span key={a}><i>{a}</i> {b}{i < all.length - 2 ? ', ' : i === all.length - 2 ? ', or ' : '.'}</span>
          ))}
        </p>
      ) : null}
      {stem ? (
        <p style={{
          margin: '10px 0 4px', padding: '12px 14px', background: theme.bg,
          borderRadius: radius.md, fontSize: 20, color: theme.text,
          lineHeight: 1.5, fontWeight: 700,
        }}>
          {stem}
        </p>
      ) : null}
      {q.item_type === 'reading' && q.payload.question ? (
        <p style={{ margin: '0 0 16px', fontSize: 17, color: theme.text2, lineHeight: 1.5 }}>
          {q.payload.question as string}
        </p>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
        {options.map((option, i) => {
          const on = picked === i
          return (
            <button
              key={i}
              type="button"
              onClick={() => setPicked(i)}
              style={{
                fontFamily: 'inherit', fontSize: 17, fontWeight: on ? 700 : 600,
                color: theme.text, textAlign: 'left',
                background: on ? theme.orangePale : '#FFFFFF',
                border: `2px solid ${on ? theme.orange : theme.border}`,
                borderRadius: radius.md, padding: '14px 16px',
                minHeight: TAP_MIN, cursor: 'pointer', lineHeight: 1.4,
                display: 'flex', alignItems: 'center',
              }}
            >
              <KeyBadge index={i} />
              <span>{option}</span>
            </button>
          )
        })}
      </div>

      <div style={{ marginTop: 22 }}>
        <Button full tone="child" disabled={picked === null || busy}
                onClick={() => onAnswer(picked)}>
          Check
        </Button>
        <TrialActions
          busy={busy}
          onReset={picked !== null ? () => setPicked(null) : undefined}
          resetLabel="Clear"
          onSkip={() => onAnswer(null, { skipped: true })}
        />
        <KeyHint text="Press a number to pick, Enter to check" />
      </div>
    </Card>
  )
}

// ------------------------------------------------------------ double blank

/** Two gaps, one pair of words, in order. Half right is wrong. */
export function DoubleBlank({ q, onAnswer, busy }: TrialProps) {
  const [picked, setPicked] = useState<number | null>(null)
  const pairs = (q.payload.options ?? []) as string[][]
  const sentence = (q.payload.sentence ?? '') as string

  useTrialKeys({
    options: pairs.length, picked, busy,
    onPick: setPicked,
    onSubmit: () => onAnswer(picked),
    onClear: () => setPicked(null),
  })

  return (
    <Card>
      <div style={{ fontSize: 14, color: theme.faint, marginBottom: 10 }}>
        Both blanks, in order
      </div>
      <p style={{ margin: '0 0 18px', fontSize: 19, color: theme.text, lineHeight: 1.6 }}>
        {sentence}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {pairs.map((pair, i) => {
          const on = picked === i
          return (
            <button
              key={i}
              type="button"
              onClick={() => setPicked(i)}
              style={{
                fontFamily: 'inherit', fontSize: 17, fontWeight: on ? 700 : 600,
                color: theme.text, textAlign: 'left',
                background: on ? theme.orangePale : '#FFFFFF',
                border: `2px solid ${on ? theme.orange : theme.border}`,
                borderRadius: radius.md, padding: '14px 16px',
                minHeight: TAP_MIN, cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
            >
              <KeyBadge index={i} />
              <span style={{ color: theme.orangeDark, fontWeight: 800 }}>{pair[0]}</span>
              <span style={{ color: theme.faint }}> … </span>
              <span style={{ color: theme.orangeDark, fontWeight: 800 }}>{pair[1]}</span>
            </button>
          )
        })}
      </div>

      <div style={{ marginTop: 22 }}>
        <Button full tone="child" disabled={picked === null || busy}
                onClick={() => onAnswer(picked)}>
          Check
        </Button>
        <TrialActions
          busy={busy}
          onReset={picked !== null ? () => setPicked(null) : undefined}
          resetLabel="Clear"
          onSkip={() => onAnswer(null, { skipped: true })}
        />
        <KeyHint text="Press a number to pick, Enter to check" />
      </div>
    </Card>
  )
}

// ------------------------------------------------------------------ router

export function Trial(props: TrialProps) {
  switch (props.q.item_type) {
    case 'learning': return <Learning {...props} />
    case 'cued_recall': return <CuedRecall {...props} />
    case 'free_recall': return <FreeRecall {...props} />
    case 'double_blank': return <DoubleBlank {...props} />
    default: return <MultipleChoice {...props} />
  }
}

/**
 * The word a "goes with" question is about, from its stored wording, which
 * varies: "Which word naturally goes with deadline?", "...goes with the word
 * 'none'?", "...goes with the target word \"will\"?". Null if it cannot be
 * found, and the stored wording is shown instead.
 */
export function collocationTarget(prompt: string): string | null {
  const m = /goes\s+(?:best\s+|naturally\s+)?(?:after|before|with)\s+(?:the\s+(?:target\s+)?word\s+)?["'“‘]?([^"'”’?]+?)["'”’]?\s*(?:to mean.*)?\?/i.exec(prompt)
  const word = m ? m[1]!.trim() : ''
  // A long catch means the stored wording added a clue ("argue to talk
  // about why..."): keep that wording rather than cut it short.
  return word && word.split(/\s+/).length <= 3 ? word : null
}

// Pairs that show what "go together" means. Three are shown, never one that
// uses a word from the question or its options: "miss a bus" next to the
// options for "deadline" would give the answer away.
const TOGETHER: [string, string][] = [
  ['deep', 'sleep'], ['heavy', 'rain'], ['brush', 'your teeth'], ['make', 'a wish'],
  ['fast', 'asleep'], ['tell', 'a story'], ['catch', 'a cold'], ['strong', 'tea'],
]
function examplesFor(prompt: string, options: string[]): [string, string][] {
  const used = new Set([prompt, ...options].join(' ').toLowerCase().split(/[^a-z]+/))
  return TOGETHER.filter(([a, b]) => ![a, ...b.split(' ')].some((w) => used.has(w))).slice(0, 3)
}
